import {
  MACRO_VARIABLES,
  type MacroVariable,
} from "@redotech/redo-support-common/macro";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoList } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, type Dispatch, type SetStateAction } from "react";
import { VariableLabel } from "./variable-label";

const SORTED_MACRO_VARIABLES: readonly MacroVariable[] = [
  ...MACRO_VARIABLES,
].sort((a, b) => a.displayName.localeCompare(b.displayName));

export function filterMacroVariables(query: string): readonly MacroVariable[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return SORTED_MACRO_VARIABLES;
  }
  return SORTED_MACRO_VARIABLES.filter(
    (v) =>
      v.displayName.toLowerCase().includes(needle) ||
      v.variable.toLowerCase().includes(needle),
  );
}

export const VariableList = memo(function VariableList({
  variables,
  focusedIndex,
  setFocusedIndex,
  onSelect,
  refToListenTo,
}: {
  variables: readonly MacroVariable[];
  focusedIndex: number | undefined;
  setFocusedIndex: Dispatch<SetStateAction<number | undefined>>;
  onSelect: (variable: MacroVariable) => void;
  refToListenTo: HTMLElement | null;
}) {
  const items: RedoListItem<MacroVariable>[] = useMemo(
    () =>
      variables.map((variable) => ({
        type: "custom" as const,
        id: variable.id,
        useWrapper: true,
        render: <VariableLabel displayName={variable.displayName} />,
        value: variable,
      })),
    [variables],
  );

  if (items.length === 0) {
    return (
      <Flex align="center" dir="column" gap="sm" p="lg">
        <Text fontSize="xs" textColor="secondary">
          No matching variables
        </Text>
      </Flex>
    );
  }

  return (
    <Flex px="xs" py="xs">
      <RedoList
        focusedIndex={focusedIndex}
        items={items}
        itemSelected={({ item }) => {
          if (item.type !== "custom") {
            return;
          }
          onSelect(item.value);
        }}
        refToListenTo={refToListenTo}
        setFocusedIndex={setFocusedIndex}
        size="sm"
      />
    </Flex>
  );
});
